import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DynamicSelect from "../../DynamicSelect";
import { FaExchangeAlt, FaTrash, FaPrint } from "react-icons/fa";
import ConfirmActionModal from "../../ConfirmDeleteModal";
import { useAuth } from "../../../utils/AuthContext";
import { PathologyPaymentSlip } from "../PathologyPaymentSlip";

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const HANDOVER_API = "https://api.muktihospital.com/api/pathology-handover";
const USER_COLLECTION_API = "https://api.muktihospital.com/api/pathology-accounts/user-collection";

const PathologyDailyClosing = () => {
  const { user } = useAuth();
  const today = new Date().toISOString().split("T")[0];
  const [date, setDate] = useState(today);

  const [staffList, setStaffList] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [toSelection, setToSelection] = useState({}); // { staffName: toUserName }
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submittingFor, setSubmittingFor] = useState(null);
  const [confirmStaff, setConfirmStaff] = useState(null);

  // Recipients for the "To" dropdown (pathologists + MD Sir/owner).
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${HANDOVER_API}/recipients-list`, { headers: { "x-api-key": API_KEY } });
        const list = res.data.data || [];
        if (!list.some((r) => r.role === "owner")) list.push({ name: "MD Sir" }); // no user has role "owner"
        setRecipients(list);
      } catch (err) {
        console.error("Recipients error:", err);
      }
    })();
  }, []);

  const fetchClosing = useCallback(async () => {
    setLoading(true);
    try {
      const [collRes, histRes] = await Promise.all([
        axios.get(USER_COLLECTION_API, {
          headers: { "x-api-key": API_KEY },
          params: { type: "daily", startDate: date, endDate: date, limit: "0", userName: user?.name || "" },
        }),
        axios.get(`${HANDOVER_API}/list`, {
          headers: { "x-api-key": API_KEY },
          params: { type: "daily", date },
        }),
      ]);
      setStaffList(collRes.data.userSummary || []);
      setHistory(histRes.data.records || []);
    } catch (err) {
      console.error("Fetch closing error:", err);
      toast.error("Failed to load daily closing.");
    } finally {
      setLoading(false);
    }
  }, [date, user?.name]);

  useEffect(() => { fetchClosing(); }, [fetchClosing]);

  const recipientOptions = recipients.map((r) => ({
    value: r.name,
    label: r.role === "owner" ? `${r.name} (MD Sir)` : r.name,
  }));

  const doHandover = async (staff) => {
    const toUser = toSelection[staff.name];
    const amount = Number(staff.netCashInHand) || 0;
    if (!toUser) return toast.error("Select who to hand over to.");
    if (toUser === staff.name) return toast.error("Cannot hand over to the same person.");
    if (amount <= 0) return toast.error("No cash to hand over.");

    setSubmittingFor(staff.name);
    try {
      await axios.post(`${HANDOVER_API}/handover`, {
        fromUser: staff.name,
        toUser,
        amount,
        handoverDate: date,
        note: `Daily closing handover (${date})`,
      }, { headers: { "x-api-key": API_KEY } });
      toast.success(`৳${amount.toFixed(2)} handed over from ${staff.name} to ${toUser}.`);
      setConfirmStaff(null);
      fetchClosing();
    } catch (err) {
      console.error("Handover error:", err);
      toast.error(err.response?.data?.error || "Handover failed.");
    } finally {
      setSubmittingFor(null);
    }
  };

  const [deleteHandoverId, setDeleteHandoverId] = useState(null);

  const handleDeleteHandover = async (id) => {
    try {
      await axios.delete(`${HANDOVER_API}/${id}`, { headers: { "x-api-key": API_KEY } });
      toast.success("Handover reversed.");
      setDeleteHandoverId(null);
      fetchClosing();
    } catch (err) {
      console.error("Delete handover error:", err);
      toast.error("Failed to reverse handover.");
    }
  };

  const totalInHand = staffList.reduce((s, u) => s + (Number(u.netCashInHand) || 0), 0);

  // ===== Payment Voucher (this staff's handovers on the selected date) =====
  const [voucherRecipient, setVoucherRecipient] = useState("ALL");

  const myHandovers = history.filter(
    (h) => h.fromUser === user?.name || h.toUser === user?.name
  );

  const voucherRecipientOptions = [
    { value: "ALL", label: "All Recipients" },
    ...Array.from(new Set(myHandovers.map((h) => (h.fromUser === user?.name ? h.toUser : h.fromUser))))
      .map((n) => ({ value: n, label: n })),
  ];

  const voucherRecords = (voucherRecipient === "ALL"
    ? myHandovers
    : myHandovers.filter((h) => h.toUser === voucherRecipient || h.fromUser === voucherRecipient)
  ).map((h) => ({
    from: h.fromUser,
    to: h.toUser,
    amount: Number(h.amount) || 0,
    type: h.fromUser === user?.name ? "Debit" : "Credit",
  }));

  const voucherNo = `PV-${date.replace(/-/g, "")}-${(user?.name || "X").replace(/\s/g, "").substring(0, 3).toUpperCase()}${voucherRecipient !== "ALL" ? "-" + voucherRecipient.replace(/\s/g, "").substring(0, 3).toUpperCase() : ""}`;

  const handlePrintVoucher = () => {
    if (voucherRecords.length === 0) return toast.error("No handover records to print for this selection.");
    setTimeout(() => window.print(), 100);
  };

  return (
    <div>
      <ToastContainer position="top-right" autoClose={2500} />
      <div className="bg-white shadow-sm rounded-lg">
        {/* Header */}
        <div className="border-b border-dashed border-M-text-color/50 p-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              <FaExchangeAlt className="text-M-primary-color" /> Daily Closing — Cash Handover
            </h2>
            <p className="text-xs text-gray-400 mt-1">Hand each staff's cash to the main pathologist, then to MD Sir.</p>
          </div>
          <div className="flex items-center gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase text-gray-400 block">Date</label>
              <input type="date" className="border p-1.5 rounded text-sm" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-right">
              <span className="text-[10px] font-black uppercase text-gray-400 block">Total In Hand</span>
              <span className="text-lg font-black text-blue-700">৳{totalInHand.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Staff table */}
        <div className="p-4" style={{ overflow: "visible" }}>
          <table className="min-w-[720px] w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-100 text-left text-[11px] uppercase font-medium text-M-heading-color">
                <th className="p-3">Staff</th>
                <th className="p-3 text-right">Net Cash in Hand</th>
                <th className="p-3">Hand Over To</th>
                <th className="p-3 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="text-center py-8 text-gray-400 text-sm">Loading...</td></tr>
              ) : staffList.length === 0 ? (
                <tr><td colSpan={4} className="text-center py-10 text-gray-400 text-sm">No staff activity on this date.</td></tr>
              ) : (
                staffList.map((staff) => {
                  const cash = Number(staff.netCashInHand) || 0;
                  return (
                    <tr key={staff.name} className="border-b hover:bg-gray-50">
                      <td className="p-3 text-sm font-semibold text-gray-800 capitalize">{staff.name?.toLowerCase()}</td>
                      <td className={`p-3 text-sm font-black text-right ${cash > 0 ? "text-emerald-600" : "text-gray-400"}`}>৳{cash.toFixed(2)}</td>
                      <td className="p-3 w-56">
                        <DynamicSelect
                          options={recipientOptions.filter((o) => o.value !== staff.name)}
                          value={recipientOptions.find((o) => o.value === toSelection[staff.name]) || null}
                          onChange={(o) => setToSelection((prev) => ({ ...prev, [staff.name]: o?.value || "" }))}
                          isSearchable={true}
                          placeholder="Select recipient..."
                        />
                      </td>
                      <td className="p-3 text-center">
                        <button
                          onClick={() => setConfirmStaff(staff)}
                          disabled={cash <= 0 || submittingFor === staff.name || !toSelection[staff.name]}
                          className="bg-M-primary-color/90 hover:bg-M-primary-color text-white text-sm py-1.5 px-5 rounded-md disabled:opacity-40 whitespace-nowrap"
                        >
                          {submittingFor === staff.name ? "Sending..." : "Handover"}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Handover history */}
        {myHandovers.length > 0 && (
          <div className="p-4 border-t border-gray-100">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <h3 className="text-sm font-bold text-gray-700">Handover History ({date})</h3>
              <div className="flex items-center gap-2">
                <div className="min-w-[180px]">
                  <DynamicSelect
                    options={voucherRecipientOptions}
                    value={voucherRecipientOptions.find((o) => o.value === voucherRecipient) || voucherRecipientOptions[0]}
                    onChange={(o) => setVoucherRecipient(o?.value || "ALL")}
                    isSearchable={true}
                    placeholder="Recipient..."
                  />
                </div>
                <button onClick={handlePrintVoucher} className="bg-M-primary-color/90 hover:bg-M-primary-color text-white text-sm py-1.5 px-4 rounded-md flex items-center gap-2 whitespace-nowrap">
                  <FaPrint size={12} /> Print Voucher
                </button>
              </div>
            </div>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b text-xs font-bold text-gray-600">
                    <th className="text-left p-2.5">From</th>
                    <th className="text-left p-2.5">To</th>
                    <th className="text-right p-2.5">Amount</th>
                    <th className="text-left p-2.5">Note</th>
                    <th className="text-center p-2.5">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {myHandovers.map((h) => (
                    <tr key={h.id} className="border-b last:border-none">
                      <td className="p-2.5 text-gray-700 capitalize">{h.fromUser?.toLowerCase()}</td>
                      <td className="p-2.5 text-gray-700 capitalize">{h.toUser?.toLowerCase()}</td>
                      <td className="p-2.5 text-right font-bold text-blue-600">৳{Number(h.amount).toFixed(2)}</td>
                      <td className="p-2.5 text-gray-500 text-xs">{h.note || "—"}</td>
                      <td className="p-2.5 text-center">
                        <button onClick={() => setDeleteHandoverId(h.id)} className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300" title="Reverse handover"><FaTrash size={12} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <ConfirmActionModal
        isOpen={!!confirmStaff}
        onClose={() => setConfirmStaff(null)}
        onConfirm={() => confirmStaff && doHandover(confirmStaff)}
        entityName="handover"
        title="Confirm Handover"
        message={confirmStaff ? `Hand over ৳${(Number(confirmStaff.netCashInHand) || 0).toFixed(2)} from ${confirmStaff.name} to ${toSelection[confirmStaff.name] || "..."}? Their cash in hand will become ৳0.` : ""}
        confirmText="Handover"
        cancelText="Cancel"
      />

      <ConfirmActionModal
        isOpen={!!deleteHandoverId}
        onClose={() => setDeleteHandoverId(null)}
        onConfirm={() => deleteHandoverId && handleDeleteHandover(deleteHandoverId)}
        entityName="handover"
        title="Reverse this handover?"
        message="This handover record will be deleted and the cash will return to the sender's balance."
        confirmText="Reverse"
        cancelText="Cancel"
      />

      <PathologyPaymentSlip
        className="payment-voucher-print"
        voucherNo={voucherNo}
        date={date}
        accountHolder={user?.name || "Staff"}
        records={voucherRecords}
      />
    </div>
  );
};

export default PathologyDailyClosing;